import {OverlayModule} from '@angular/cdk/overlay';
import {
  afterNextRender,
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  ElementRef,
  forwardRef,
  inject,
  Injector,
  input,
  model,
  output,
  signal,
  untracked,
  viewChild,
} from '@angular/core';
import {AbstractControl, ControlValueAccessor, NG_VALIDATORS, NG_VALUE_ACCESSOR, ValidationErrors, Validator} from '@angular/forms';
import {DateFormatService} from '../date-format/date-format.service';
import {OverlayPanelDirective} from '../overlay/overlay-panel.directive';
import {HourFormat, TimeOption, TimePickerError} from './time-picker';
import {DEFAULT_INTERVAL} from './time-picker.constants';
import {formatTime, intervalTimes, parseTime, toMinutes} from './time-parse';

let nextId = 0;

/**
 * A time-of-day field: typeable, with a clock button and a dropdown list of times to pick from, in
 * 12h (`3:42 pm`) or 24h (`15:42`). The value is always `HH:mm` 24h — or `null` while unset — so
 * the backend contract is the same whichever way the field reads.
 *
 * - **Format:** `hourFormat`, or the selected company's profile when it is not given (US → 12h).
 * - **Typing** follows {@link parseTime}. The value is committed on blur or Enter, never mid-word.
 *   Text that does not parse, or falls outside `min`/`max`, stays on screen flagged invalid and
 *   leaves the value alone; what parses is rewritten canonically (`3:2p` → `3:02 pm`).
 * - **The list** opens from the clock button, a click on the field (`openOnClick`) or Up/Down. It
 *   marks the selected time, scrolls it into view and activates the closest row when the value is
 *   off the grid. Up/Down move the active row, Enter picks it, Escape and blur close. Focus never
 *   leaves the field.
 * - **Forms:** a `ControlValueAccessor` and a `Validator` (`timeParse` / `timeMin` / `timeMax`);
 *   `time` is a model for plain two-way binding. `invalid` is public, for a host drawing its own
 *   error state around a `bare` field.
 */
@Component({
  selector: 'app-time-picker',
  exportAs: 'appTimePicker',
  imports: [OverlayModule, OverlayPanelDirective],
  templateUrl: './time-picker.component.html',
  styleUrl: './time-picker.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {class: 'app-time-picker', '[class.bare]': 'bare()'},
  providers: [
    {provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => TimePickerComponent), multi: true},
    {provide: NG_VALIDATORS, useExisting: forwardRef(() => TimePickerComponent), multi: true},
  ],
})
export class TimePickerComponent implements ControlValueAccessor, Validator {
  /** The committed time, `HH:mm` 24h, or `null` while unset. */
  readonly time = model<string | null>(null);
  /** `12h` or `24h`; `null` follows the selected company's profile. */
  readonly hourFormat = input<HourFormat | null>(null);
  /** Minutes between the listed times. Ignored when `options` is given. */
  readonly interval = input(DEFAULT_INTERVAL);
  /** An explicit list of `HH:mm` times, replacing the interval-generated one. */
  readonly options = input<readonly string[] | null>(null);
  /** Inclusive `HH:mm` bounds: times outside them are not listed, and typing one is invalid. */
  readonly min = input<string | null>(null);
  readonly max = input<string | null>(null);
  /** Clicking the field opens the list, not only the clock button. */
  readonly openOnClick = input(true);
  /** Emptying the field unsets the value. Without it, an emptied field reverts. */
  readonly clearable = input(false);
  /** Shown greyed while no time is set. */
  readonly placeholder = input('');
  /** The input's accessible name, for a field without a visible label. */
  readonly ariaLabel = input<string | null>(null);
  readonly disabled = input(false);
  /** No fill, padding or clock button: plain text inside a host's own pill. */
  readonly bare = input(false);

  /** A time was picked from the list. */
  readonly selected = output<string>();
  readonly opened = output<void>();
  readonly closed = output<void>();

  private readonly formats = inject(DateFormatService);
  private readonly injector = inject(Injector);
  private readonly inputRef = viewChild.required<ElementRef<HTMLInputElement>>('input');
  private readonly listRef = viewChild<ElementRef<HTMLElement>>('list');

  protected readonly listId = `app-time-picker-${nextId++}-list`;

  protected readonly open = signal(false);
  /** The row Up/Down/Enter act on, as an index into {@link listOptions}. */
  protected readonly activeIndex = signal<number | null>(null);
  /** Raw text while the user is typing, or text a commit rejected; `null` shows the value. */
  private readonly draft = signal<string | null>(null);
  /** The active row was reached with the arrow keys since the last keystroke, so Enter picks it. */
  private readonly navigated = signal(false);
  private readonly formDisabled = signal(false);

  protected readonly isDisabled = computed(() => this.disabled() || this.formDisabled());
  protected readonly effectiveHourFormat = computed<HourFormat>(() =>
    this.hourFormat() ?? (this.formats.is12h() ? '12h' : '24h'));

  protected readonly listOptions = computed<TimeOption[]>(() => {
    const min = this.min();
    const max = this.max();
    const hourFormat = this.effectiveHourFormat();
    return (this.options() ?? intervalTimes(this.interval()))
      .filter(value => (!min || value >= min) && (!max || value <= max))
      .map(value => ({value, label: formatTime(value, hourFormat)}));
  });

  /** What the input shows: the draft, else the committed time in the field's format. */
  protected readonly text = computed(() => {
    const draft = this.draft();
    if (draft !== null) {
      return draft;
    }
    const time = this.time();
    return time ? formatTime(time, this.effectiveHourFormat()) : '';
  });

  /**
   * What is wrong, if anything: the draft does not parse, or it — or, with no draft, the value —
   * falls outside the bounds. An emptied draft is no error: it unsets or reverts on commit.
   */
  readonly error = computed<TimePickerError | null>(() => {
    const draft = this.draft();
    if (draft !== null) {
      if (draft.trim() === '') {
        return null;
      }
      const parsed = this.parse(draft);
      return parsed === null ? 'timeParse' : this.boundsError(parsed);
    }
    const time = this.time();
    return time ? this.boundsError(time) : null;
  });
  readonly invalid = computed(() => this.error() !== null);

  protected readonly activeOptionId = computed(() => {
    const index = this.activeIndex();
    return index === null ? null : this.optionId(index);
  });

  private onChange: (value: string | null) => void = () => undefined;
  private onTouched: () => void = () => undefined;
  private onValidatorChange: () => void = () => undefined;

  constructor() {
    // A form re-runs `validate` only when asked; the error changes with typing, not with the value.
    effect(() => {
      this.error();
      untracked(() => this.onValidatorChange());
    });
  }

  writeValue(value: string | null): void {
    this.time.set(value ? parseTime(value, '24h') : null);
    this.draft.set(null);
  }

  registerOnChange(fn: (value: string | null) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  registerOnValidatorChange(fn: () => void): void {
    this.onValidatorChange = fn;
  }

  setDisabledState(disabled: boolean): void {
    this.formDisabled.set(disabled);
    if (disabled) {
      this.closeList();
    }
  }

  validate(_control: AbstractControl): ValidationErrors | null {
    switch (this.error()) {
      case 'timeParse':
        return {timeParse: {text: this.draft()}};
      case 'timeMin':
        return {timeMin: {min: this.min(), actual: this.pendingTime()}};
      case 'timeMax':
        return {timeMax: {max: this.max(), actual: this.pendingTime()}};
      default:
        return null;
    }
  }

  /** Clicking anywhere in the field focuses the input and selects it, so typing replaces. */
  protected onFieldClick(): void {
    if (this.isDisabled()) {
      return;
    }
    const input = this.inputRef().nativeElement;
    input.focus();
    input.select();
    if (this.openOnClick()) {
      this.openList();
    }
  }

  /**
   * A click into the already-focused field selects it all again. Chrome collapses a selection the
   * user clicks inside only *after* the click event, which would undo {@link onFieldClick}'s
   * `select()`; cancelling the press keeps the caret from landing at all. An unfocused field is
   * left to focus normally, and is selected on the click that follows.
   */
  protected onInputMousedown(event: MouseEvent): void {
    const input = this.inputRef().nativeElement;
    if (document.activeElement === input) {
      event.preventDefault();
      input.select();
    }
  }

  protected toggleList(): void {
    if (this.isDisabled()) {
      return;
    }
    this.inputRef().nativeElement.focus();
    if (this.open()) {
      this.closeList();
    } else {
      this.openList();
    }
  }

  protected type(text: string): void {
    this.draft.set(text);
    this.navigated.set(false);
    if (this.open()) {
      const parsed = this.parse(text);
      if (parsed !== null) {
        this.activeIndex.set(this.closestIndex(parsed));
        this.scrollToActive('nearest');
      }
    }
  }

  protected onKeydown(event: KeyboardEvent): void {
    switch (event.key) {
      case 'ArrowDown':
      case 'ArrowUp':
        event.preventDefault();
        this.moveActive(event.key === 'ArrowDown' ? 1 : -1);
        break;
      case 'Enter':
        // Never submit a form the field sits in.
        event.preventDefault();
        this.onEnter();
        break;
      case 'Escape':
        if (this.open()) {
          // Closing the list is all Escape does here; a dialog around the field stays open.
          event.preventDefault();
          event.stopPropagation();
          this.closeList();
        }
        break;
    }
  }

  protected onBlur(): void {
    this.commit();
    this.closeList();
    this.onTouched();
  }

  protected pick(option: TimeOption): void {
    this.draft.set(null);
    if (option.value !== this.time()) {
      this.setTime(option.value);
    }
    this.syncInput();
    this.selected.emit(option.value);
    this.closeList();
  }

  protected closeList(): void {
    if (!this.open()) {
      return;
    }
    this.open.set(false);
    this.activeIndex.set(null);
    this.navigated.set(false);
    this.closed.emit();
  }

  protected optionId(index: number): string {
    return `${this.listId}-${index}`;
  }

  private openList(): void {
    if (this.open() || this.isDisabled()) {
      return;
    }
    const target = this.pendingTime();
    this.activeIndex.set(target === null ? null : this.closestIndex(target));
    this.navigated.set(false);
    this.open.set(true);
    this.opened.emit();
    // The rows only exist once the overlay has rendered.
    afterNextRender(() => this.scrollToActive('center'), {injector: this.injector});
  }

  /** Opens the list, or moves the active row by one, stopping at either end. */
  private moveActive(step: 1 | -1): void {
    if (!this.open()) {
      this.openList();
      return;
    }
    const count = this.listOptions().length;
    if (count === 0) {
      return;
    }
    const current = this.activeIndex();
    const next = current === null
      ? (step === 1 ? 0 : count - 1)
      : Math.min(Math.max(current + step, 0), count - 1);
    this.activeIndex.set(next);
    this.navigated.set(true);
    this.scrollToActive('nearest');
  }

  /**
   * Enter picks the active row when the arrows led there, or when nothing was typed; otherwise it
   * commits the typed text — the row merely tracking the typing must not overrule it.
   */
  private onEnter(): void {
    const index = this.activeIndex();
    const option = index === null ? undefined : this.listOptions()[index];
    if (this.open() && option && (this.draft() === null || this.navigated())) {
      this.pick(option);
      return;
    }
    this.commit();
    this.closeList();
  }

  /**
   * Commits the draft: an emptied field unsets (`clearable`) or reverts, a valid time is set and
   * shown canonically, and anything else stays on screen, flagged by {@link error}.
   */
  private commit(): void {
    const text = this.draft();
    if (text === null) {
      return;
    }
    if (text.trim() === '') {
      this.draft.set(null);
      if (this.clearable() && this.time() !== null) {
        this.setTime(null);
      }
      this.syncInput();
      return;
    }
    const parsed = this.parse(text);
    if (parsed === null || this.boundsError(parsed) !== null) {
      return;
    }
    this.draft.set(null);
    if (parsed !== this.time()) {
      this.setTime(parsed);
    }
    this.syncInput();
  }

  private setTime(value: string | null): void {
    this.time.set(value);
    this.onChange(value);
  }

  /**
   * Writes the shown text straight to the element. A commit that leaves the value as it was (or
   * rewrites `3:2p` to the `3:02 pm` already bound) changes nothing Angular can see, so the
   * `[value]` binding alone would leave the typed text on screen.
   */
  private syncInput(): void {
    this.inputRef().nativeElement.value = this.text();
  }

  private parse(text: string): string | null {
    return parseTime(text, this.effectiveHourFormat());
  }

  /** The time the field currently stands for: a parseable draft, else the value. */
  private pendingTime(): string | null {
    const draft = this.draft();
    return (draft === null ? null : this.parse(draft)) ?? this.time();
  }

  private boundsError(value: string): TimePickerError | null {
    const min = this.min();
    const max = this.max();
    if (min && value < min) return 'timeMin';
    return max && value > max ? 'timeMax' : null;
  }

  /** The row holding `value`, or the one nearest to it; `null` for an empty list. */
  private closestIndex(value: string): number | null {
    const options = this.listOptions();
    if (options.length === 0) {
      return null;
    }
    const target = toMinutes(value);
    let best = 0;
    options.forEach((option, index) => {
      if (Math.abs(toMinutes(option.value) - target) < Math.abs(toMinutes(options[best].value) - target)) {
        best = index;
      }
    });
    return best;
  }

  /**
   * Scrolls the list itself (never the page) so the active row shows: centred on opening, just
   * into view while moving. Relies on the list being the rows' offset parent.
   */
  private scrollToActive(block: 'center' | 'nearest'): void {
    const list = this.listRef()?.nativeElement;
    const id = this.activeOptionId();
    const row = id ? list?.querySelector<HTMLElement>(`#${id}`) : null;
    if (!list || !row) {
      return;
    }
    if (block === 'center') {
      list.scrollTop = row.offsetTop - (list.clientHeight - row.offsetHeight) / 2;
    } else if (row.offsetTop < list.scrollTop) {
      list.scrollTop = row.offsetTop;
    } else if (row.offsetTop + row.offsetHeight > list.scrollTop + list.clientHeight) {
      list.scrollTop = row.offsetTop + row.offsetHeight - list.clientHeight;
    }
  }
}
